import React, { useState, useEffect } from 'react';
import { useTranslation } from 'next-i18next';
import clsx from 'clsx';
import { useBucket } from '@context/bucket.context';
import { Product } from '@interfaces/models/product';
import sanitizer from '@helpers/sanitizer';
import { LDState } from '@components/bucketTest/types';
import BuyerFeesModal from '@components/buyer-fees/buyer-fees-modale/buyer-fees-modale';
import Icon from '@components/atoms/icon/icon';
import { mapToMinimumPricingBreakdown } from '@maps/pricingBreakdown/pricingBreakdown';
import ProductPriceSeller from './product-price-seller/product-price-seller';
import styles from './product-price.module.scss';

export type PriceProps = {
  product?: Product;
  isSeller?: boolean;
  directShippingEligible?: boolean;
  customPriceClass?: string;
  hasMandatoryAuthentication?: boolean;
};

const ProductPrice: React.FC<PriceProps> = (props) => {
  const {
    product,
    isSeller = false,
    directShippingEligible,
    customPriceClass = '',
    hasMandatoryAuthentication = false,
  } = props;

  const { t } = useTranslation();
  const { t: tA11y } = useTranslation('a11y');
  const { state, isFeatureEnabled } = useBucket();
  const isBuyer = !isSeller;

  const [shouldShowProductPriceDescription, setShouldShowProductPriceDescription] = useState<boolean>(false);

  const isProductSold: boolean = product?.sold;
  const isAuthenticationFree = !(product?.buyerFees && product.buyerFees?.[0]?.cost?.cents > 0);
  const hasPromotionApplied: boolean = product.regularPrice && product.regularPrice.cents > 0;
  const priceBreakdown = mapToMinimumPricingBreakdown(product.pricingBreakdown);
  const enableTariffsVisibilityConex = isFeatureEnabled((f) => f.enableTariffsVisibilityConex, true);
  const displayUsDutiesMessage = enableTariffsVisibilityConex && product?.hasNewTaxes;

  useEffect(() => {
    if ([LDState.FAILED, LDState.SUCCESS].includes(state)) {
      setShouldShowProductPriceDescription(true);
    }
  }, [state]);

  return (
    <div className={clsx(styles.productPrice)}>
      {/* If product is still available */}
      {!isProductSold && (
        <>
          {(!priceBreakdown || (priceBreakdown && isBuyer)) && (
            <p className={styles.productPrice__price}>
              {hasPromotionApplied && (
                <span
                  className={styles['productPrice__price--strikeOut']}
                  data-cy="product_price_drop"
                >
                  {product?.regularPrice?.formatted}
                </span>
              )}
              <span
                className={clsx(customPriceClass, hasPromotionApplied && styles['productPrice__price--promo'])}
                data-cy="product_price"
              >
                {product?.price?.formatted}
              </span>
              {priceBreakdown && !isProductSold && !isSeller && (
                <span className={styles.productPrice__InfoBox}>
                  <BuyerFeesModal
                    breakdown={{
                      ...priceBreakdown,
                      includesAuthenticationFees: hasMandatoryAuthentication,
                    }}
                    productLocation={product.seller.country}
                    trackingContext="pdp"
                    productId={product.id}
                  >
                    <Icon
                      name="infobox-default"
                      data-cy="product_price_info_icon"
                      ariaLabel={tA11y('A11Y.ICON.INFO_BUYER_FEE')}
                    />
                  </BuyerFeesModal>
                </span>
              )}
            </p>
          )}
          {displayUsDutiesMessage && (
            <p
              className={styles.productPrice__buyerFees}
              data-cy="product_price_us_duties"
            >
              {t('PRODUCT.DETAILS.DUTIES_MESSAGE', 'Duties calculated at checkout')}
            </p>
          )}
          {priceBreakdown && isSeller && (
            <ProductPriceSeller
              breakdown={{
                ...priceBreakdown,
                includesAuthenticationFees: hasMandatoryAuthentication,
              }}
              productId={product.id}
            />
          )}
          {shouldShowProductPriceDescription && (
            <>
              {hasMandatoryAuthentication || directShippingEligible ? null : isAuthenticationFree ? (
                <p
                  className={styles.productPrice__buyerFees}
                  data-cy="product_price_free_buyer_fee"
                  dangerouslySetInnerHTML={{
                    __html: sanitizer(t('PRODUCT.DETAILS.BUYER_FEES.FREE')),
                  }}
                />
              ) : (
                <p
                  className={styles.productPrice__buyerFees}
                  data-cy="product_price_buyer_fee_amount"
                >
                  {t('PRODUCT.DETAILS.BUYER_FEES.AMOUNT', {
                    product,
                  })}
                </p>
              )}
            </>
          )}
        </>
      )}
      {/* If product has already been sold or if the product has been reserved on BO */}
      {isProductSold &&
        (!priceBreakdown || (priceBreakdown && isBuyer) ? (
          <span
            className={styles.productPrice__sold}
            data-cy="product_price_sold"
          >
            {t('PRODUCT.DETAILS.SOLD.RESERVED', {
              price_formatted: product.price.formatted,
            })}
          </span>
        ) : (
          <ProductPriceSeller
            breakdown={{
              ...priceBreakdown,
              includesAuthenticationFees: hasMandatoryAuthentication,
            }}
            isSold
            productId={product.id}
          />
        ))}
    </div>
  );
};

export default ProductPrice;
